// Stroke Analysis Module - Pure stroke detection and analysis
// No caching, no database queries - just analysis

/* eslint-disable no-console */
import { StrokeData, ParameterInfo } from './telemetryService';

// Functions will be implemented in subsequent tasks
export function calculateStrokeRate(telemetryData: any[], _parameters?: ParameterInfo): number {
  if (!telemetryData || telemetryData.length < 10) {
    console.log(
      '🔍 STROKE_ANALYSIS: Insufficient data points for stroke rate calculation:',
      telemetryData?.length || 0
    );
    return 0;
  }

  const strokeIntervals: number[] = [];
  const forceValues: number[] = [];
  const timeValues: number[] = [];

  // Extract force and time data from all rowers (aggregate approach)
  for (const point of telemetryData) {
    // Database stores timestamp as BIGINT (milliseconds since session start)
    // Peach files use milliseconds from session start (0, 20, 40, 60, etc.)
    let currentTime: number;

    if (typeof point.timestamp === 'number') {
      currentTime = point.timestamp;
    } else if (typeof point.timestamp === 'string') {
      // Handle string timestamps (shouldn't happen with current DB schema)
      currentTime = parseInt(point.timestamp) || 0;
    } else {
      // Skip invalid timestamps
      continue;
    }

    const gateForce = point.gate_force_x;

    if (gateForce && typeof gateForce === 'number' && gateForce > 0) {
      forceValues.push(gateForce);
      timeValues.push(currentTime);
    }
  }

  // Validate we have enough force data points
  if (forceValues.length < 10) {
    console.log(
      '🔍 STROKE_ANALYSIS: Insufficient force data points after filtering:',
      forceValues.length
    );
    return 0;
  }

  // Find stroke peaks using adaptive peak detection
  const strokePeaks: number[] = [];

  // Adaptive threshold based on data characteristics
  const maxForce = Math.max(...forceValues);
  const minPeakHeight = Math.max(10, maxForce * 0.3); // 30% of max force or 10kgf minimum

  // Calculate expected stroke rate from data duration
  const dataDurationMs = timeValues[timeValues.length - 1] - timeValues[0];
  const expectedStrokesPerMinute = 30; // Typical rowing stroke rate
  const expectedStrokeIntervalMs = (60 * 1000) / expectedStrokesPerMinute;
  const minPeakDistance = Math.max(800, expectedStrokeIntervalMs * 0.5); // 50% of expected interval or 800ms minimum

  // Debug logging
  console.log('🔍 STROKE_ANALYSIS: Data analysis:', {
    totalPoints: forceValues.length,
    dataDurationMs,
    maxForce,
    minPeakHeight,
    minPeakDistance,
    expectedStrokesPerMinute,
  });

  for (let i = 5; i < forceValues.length - 5; i++) {
    const current = forceValues[i];

    // Check if this is a local maximum above threshold
    if (current > minPeakHeight) {
      // Check if it's higher than surrounding points (local maximum)
      let isLocalMax = true;
      for (let j = i - 5; j <= i + 5; j++) {
        if (j !== i && forceValues[j] >= current) {
          isLocalMax = false;
          break;
        }
      }

      if (isLocalMax) {
        const currentTime = timeValues[i];

        // Check minimum distance from last peak
        if (
          strokePeaks.length === 0 ||
          currentTime - strokePeaks[strokePeaks.length - 1] >= minPeakDistance
        ) {
          strokePeaks.push(currentTime);
        }
      }
    }
  }

  // Calculate intervals between peaks
  for (let i = 1; i < strokePeaks.length; i++) {
    const interval = strokePeaks[i] - strokePeaks[i - 1];
    strokeIntervals.push(interval);
  }

  if (strokeIntervals.length === 0) {
    console.log('🔍 STROKE_ANALYSIS: No stroke intervals detected');
    return 0;
  }

  // Calculate average interval and convert to strokes per minute
  const averageIntervalMs =
    strokeIntervals.reduce((sum, interval) => sum + interval, 0) / strokeIntervals.length;
  const strokesPerMinute = (60 * 1000) / averageIntervalMs;

  console.log('🔍 STROKE_ANALYSIS: Stroke rate calculation:', {
    strokeIntervals: strokeIntervals.length,
    averageIntervalMs,
    strokesPerMinute: Math.round(strokesPerMinute),
  });

  return Math.round(strokesPerMinute);
}

export function processStrokeDataForSignature(
  rawData: StrokeData[],
  maxStrokes: number = 3
): StrokeData[] {
  if (!rawData.length) return [];

  // FIXED: Preserve temporal sequence - don't sort by angle first
  // Sorting by angle destroys the natural stroke progression
  // Instead, maintain time-based sequence and detect strokes temporally

  // Find stroke boundaries by detecting catch positions in temporal order
  const strokeBoundaries: number[] = [];
  for (let i = 0; i < rawData.length - 1; i++) {
    const current = rawData[i];
    const next = rawData[i + 1];

    // FIXED: Updated thresholds based on rowing biomechanics table and real data analysis
    // Biomechanics table shows elite catch: -60° to -50°, using -50° as optimal threshold
    // Real data shows catch forces 20-30 kgf, updated from unrealistic 10 kgf threshold
    if (current.angle < -50 && current.force < 20 && next.force > current.force + 8) {
      strokeBoundaries.push(i);
    }
  }

  // If we can't detect strokes automatically, create artificial cycles
  if (strokeBoundaries.length === 0) {
    const chunkSize = Math.floor(rawData.length / maxStrokes);
    if (chunkSize > 5) {
      for (let i = 0; i < maxStrokes; i++) {
        strokeBoundaries.push(i * chunkSize);
      }
    }
  }

  // Process each stroke cycle
  const processedData: StrokeData[] = [];
  const useableStrokes = Math.min(strokeBoundaries.length, maxStrokes);

  for (let strokeNum = 0; strokeNum < useableStrokes; strokeNum++) {
    const startIdx = strokeBoundaries[strokeNum] || 0;
    const endIdx = strokeBoundaries[strokeNum + 1] || rawData.length;
    const strokeData = rawData.slice(startIdx, endIdx); // Use original temporal order

    if (strokeData.length < 5) continue; // Skip incomplete strokes

    // FIXED: Updated phase separation thresholds based on real competitive data analysis
    // Real competitive data shows recovery forces 4-20 kgf, drive forces 20+ kgf
    // Previous threshold (5 kgf) classified 99% of data as drive phase
    const recoveryPhase = strokeData
      .filter((p) => p.force < 20) // FIXED: Realistic recovery threshold
      .sort((a, b) => b.angle - a.angle) // Right to left (decreasing angle)
      .map((point, _index) => ({
        ...point,
        phase: 'recovery',
        strokeNumber: strokeNum + 1,
      }));

    const drivePhase = strokeData
      .filter((p) => p.force >= 20) // FIXED: Realistic drive threshold
      .sort((a, b) => a.angle - b.angle) // Left to right (increasing angle)
      .map((point, _index) => ({
        ...point,
        phase: 'drive',
        strokeNumber: strokeNum + 1,
      }));

    // Add recovery first, then drive to create clockwise loop
    processedData.push(...recoveryPhase, ...drivePhase);
  }

  return processedData;
}

export function detectStrokeBoundaries(
  telemetryData: any[],
  driveThreshold: number
): Array<{ start: number; end: number | null }> {
  const strokeBoundaries: Array<{ start: number; end: number | null }> = [];

  if (!telemetryData || telemetryData.length === 0) {
    return strokeBoundaries;
  }

  let inDrivePhase = false;
  let currentStrokeStart: number | null = null;

  telemetryData.forEach((point: any) => {
    const gateForceKgf = Math.abs(point.gate_force_x || 0);

    // Stroke boundary detection with hysteresis
    if (!inDrivePhase && gateForceKgf >= driveThreshold) {
      // Start of new drive phase
      if (currentStrokeStart !== null) {
        // Complete previous stroke
        strokeBoundaries.push({ start: currentStrokeStart, end: point.timestamp });
      }
      inDrivePhase = true;
      currentStrokeStart = point.timestamp;
    } else if (inDrivePhase && gateForceKgf < driveThreshold * 0.5) {
      // End of drive phase detected
      // Using 50% of start threshold for hysteresis
      inDrivePhase = false;
    }
  });

  // Handle final stroke (no end detected)
  if (currentStrokeStart !== null) {
    strokeBoundaries.push({ start: currentStrokeStart, end: null });
  }

  return strokeBoundaries;
}
